import { intersection } from "lodash";

import signalEventsService from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalEventsService";
import type {
    SignalEventType,
    SignalEventsTypeItem,
} from "app/pages/sales-intelligence/modules/company-analysis/services/signals/types";
import type {
    NewsSignalEventDto,
    SignalEventDto,
} from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import type NewsSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalFilters";
import { splitArray } from "app/pages/sales-intelligence/utils/arrays";

import { compareSignalEventsByDate } from "../../../../utils/signalsEvents";
import type SignalEventsTooltipViewModel from "../SignalEventsTooltipViewModel";
import type SignalEventsViewModelFactoryDeps from "../SignalEventsViewModelFactoryDeps";

type FactoryDeps = Omit<SignalEventsViewModelFactoryDeps<NewsSignalEventDto, NewsSignalFilters>, "preferredSignals"> & {
    preferredSignals: readonly Signal<NewsSignalFilters>[];
};

export default function createNewsSignalsTooltipViewModel(deps: FactoryDeps): Readonly<SignalEventsTooltipViewModel> {
    const { eventsLimit, preferredSignals, signalEvents, companyInfo, signalsGroupedByType } = deps;

    const validSignalEvents = signalEventsService.getValidSignalEvents(signalEvents);
    const sortedSignalEvents = [...validSignalEvents].sort(compareSignalEventsByDate);

    function matchSignalEventToPreferredSignal({ categories }: Readonly<NewsSignalEventDto>): boolean {
        return preferredSignals.some(({ filters }) => {
            const commonItems = intersection(filters.categories, categories);

            return commonItems.length > 0;
        });
    }

    const [appliedSignalEventsList, otherSignalsEvents] = splitArray(
        sortedSignalEvents,
        matchSignalEventToPreferredSignal,
        eventsLimit,
    );

    return {
        get signalEventsList(): readonly SignalEventDto[] {
            return appliedSignalEventsList;
        },

        get signalEventsTypeItems(): readonly SignalEventsTypeItem[] {
            if (otherSignalsEvents.length === 0) {
                return [];
            }

            return signalEventsService
                .getOrderedSignalEventsTypeItems(otherSignalsEvents, signalsGroupedByType)
                .filter(signalEventsService.isValidSignalEventTypeItem);
        },

        getSignalEventType(signalEvent: Readonly<SignalEventDto>): SignalEventType {
            return signalEventsService.getSignalEventType(signalEvent, signalsGroupedByType);
        },

        matchEventTitle(signalEvent: Readonly<SignalEventDto>): string {
            return signalEventsService.getSignalEventTitle(signalEvent, companyInfo);
        },
    };
}
